import React, { useEffect, useState } from "react";
import { api, apiErrorMessage, formatCurrency, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, MagnifyingGlass, Storefront } from "@phosphor-icons/react";

const EMPTY = { company: "", contact_person: "", mobile: "", whatsapp: "", email: "", address: "", gst_number: "" };

export default function Suppliers() {
    const [items, setItems] = useState([]);
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/suppliers", { params: search ? { search } : {} });
            setItems(data);
        } catch (e) { toast.error(apiErrorMessage(e)); } finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [search]);

    const openCreate = () => { setEditing(null); setForm(EMPTY); setDialogOpen(true); };
    const openEdit = (s) => { setEditing(s); setForm({ ...EMPTY, ...s }); setDialogOpen(true); };

    const submit = async () => {
        if (!form.company.trim()) return toast.error("Company required");
        try {
            if (editing) await api.patch(`/suppliers/${editing.id}`, form);
            else await api.post("/suppliers", form);
            toast.success(editing ? "Supplier updated" : "Supplier created");
            setDialogOpen(false); load();
        } catch (e) { toast.error(apiErrorMessage(e)); }
    };

    return (
        <div className="space-y-6" data-testid="suppliers-page">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                <div>
                    <div className="eyebrow mb-2">Contacts</div>
                    <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight font-display">Suppliers</h1>
                    <p className="text-sm text-neutral-500 mt-1.5">{items.length} supplier{items.length !== 1 && "s"}.</p>
                </div>
                <Button onClick={openCreate} className="text-white h-11" style={{ backgroundColor: "#15803D" }} data-testid="suppliers-add-btn">
                    <Plus size={16} weight="bold" className="mr-1.5" /> New Supplier
                </Button>
            </div>

            <div className="relative">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                <Input placeholder="Search company, mobile..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-11" data-testid="suppliers-search-input" />
            </div>

            <div className="bg-white border border-neutral-200 rounded-md overflow-hidden" data-testid="suppliers-table">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
                            <tr>
                                <th className="text-left px-5 py-3 font-semibold">Company</th>
                                <th className="text-left px-5 py-3 font-semibold">Contact</th>
                                <th className="text-left px-5 py-3 font-semibold">Mobile</th>
                                <th className="text-left px-5 py-3 font-semibold">Email</th>
                                <th className="text-left px-5 py-3 font-semibold">GST</th>
                                <th className="text-right px-5 py-3 font-semibold">Outstanding</th>
                                <th className="px-5 py-3"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && <tr><td colSpan={7} className="text-center py-10 text-neutral-400">Loading...</td></tr>}
                            {!loading && items.length === 0 && (
                                <tr><td colSpan={7} className="text-center py-14 text-neutral-400">
                                    <Storefront size={30} weight="thin" className="mx-auto mb-2 text-neutral-300" />
                                    No suppliers yet.
                                </td></tr>
                            )}
                            {items.map((s) => (
                                <tr key={s.id} className="data-row border-t border-neutral-100" data-testid={`supplier-row-${s.company}`}>
                                    <td className="px-5 py-3 font-semibold">{s.company}</td>
                                    <td className="px-5 py-3 text-neutral-600">{s.contact_person || "-"}</td>
                                    <td className="px-5 py-3 text-neutral-600">{s.mobile || "-"}</td>
                                    <td className="px-5 py-3 text-neutral-600">{s.email || "-"}</td>
                                    <td className="px-5 py-3 text-xs font-mono text-neutral-500">{s.gst_number || "-"}</td>
                                    <td className="px-5 py-3 text-right font-semibold tabular">{formatCurrency(s.outstanding_balance)}</td>
                                    <td className="px-5 py-3 text-right">
                                        <button onClick={() => openEdit(s)} className="text-neutral-500 hover:text-neutral-900" data-testid={`supplier-edit-${s.company}`}>
                                            <Pencil size={14} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent data-testid="supplier-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-2xl font-extrabold font-display">{editing ? "Edit Supplier" : "New Supplier"}</DialogTitle>
                    </DialogHeader>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <Field label="Company *"><Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="supplier-form-company" /></Field>
                        <Field label="Contact Person"><Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} data-testid="supplier-form-contact" /></Field>
                        <Field label="Mobile"><Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} data-testid="supplier-form-mobile" /></Field>
                        <Field label="WhatsApp"><Input value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} data-testid="supplier-form-whatsapp" /></Field>
                        <Field label="Email"><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="supplier-form-email" /></Field>
                        <Field label="GST Number"><Input value={form.gst_number} onChange={(e) => setForm({ ...form, gst_number: e.target.value })} data-testid="supplier-form-gst" /></Field>
                    </div>
                    <Field label="Address"><Textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} rows={2} data-testid="supplier-form-address" /></Field>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="supplier-form-cancel">Cancel</Button>
                        <Button onClick={submit} className="text-white" style={{ backgroundColor: "#15803D" }} data-testid="supplier-form-submit">
                            {editing ? "Save Changes" : "Create"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-neutral-600 mb-1.5 block">{label}</Label>
            {children}
        </div>
    );
}
